import { createWithEqualityFn } from "zustand/traditional";

import { stages } from "../../screens/auth/get-started/getStartedConfig";
import type { UserSegmentationData } from "./types";

// stageId → optionId[]
type Answers = Record<string, string[]>;

export type { UserSegmentationData };

interface StoreState {
  step: number; // 0-based index into `stages`
  // This is a record of the answers to the questions. The key is the stageId
  // and the value is an array of the optionIds that the user has selected.
  answers: Answers;
  isSignUpOnboarding: boolean;
  userSegmentationData: UserSegmentationData;
  shouldShowReferralModal: boolean;
}

const initialState: StoreState = {
  step: 0,
  answers: {},
  isSignUpOnboarding: false,
  userSegmentationData: {
    useCases: undefined,
    usageScopes: undefined,
    features: undefined,
  },
  shouldShowReferralModal: false,
};

type Action = {
  // select / toggle an option bubble
  toggleOption: (optionId: string) => void;
  // advance to the next stage and persist if needed
  next: () => void;
  setUserSegmentationData: (data: Partial<UserSegmentationData>) => void;
  setIsSignUpOnboarding: (isSignUpOnboarding: boolean) => void;
  setShouldShowReferralModal: (shouldShow: boolean) => void;
  reset: () => void;
};

export const useSignUpOnboardingStore = createWithEqualityFn<
  StoreState & Action
>(set => ({
  ...initialState,
  // select is used to toggle an option bubble. It is used to select and
  // deselect options.
  toggleOption: optId =>
    set(s => {
      const stage = stages[s.step]!;
      const prev = s.answers[stage.id] ?? [];
      const next = prev.includes(optId)
        ? prev.filter(id => id !== optId) // deselect
        : [...prev, optId]; // select

      return { answers: { ...s.answers, [stage.id]: next } };
    }),
  // next writes the array of selected options to the userSegmentationData
  // determined by the stage.choices.storeKey
  next: () =>
    set(s => {
      const stage = stages[s.step]!;
      const selections = s.answers[stage.id] ?? []; // array, might be empty

      if (stage.choices?.storeKey && selections.length) {
        s.userSegmentationData = {
          ...s.userSegmentationData,
          [stage.choices.storeKey]: selections, // store whole array
        };
      }

      // Harmless defensive check to prevent out of bounds access out of the
      // stages array.
      // Users will always exit the onboarding flow after the last stage.In GetStartedNextButton.tsx
      return { step: Math.min(s.step + 1, stages.length - 1) };
    }),
  setUserSegmentationData: data =>
    set(state => ({
      userSegmentationData: {
        ...state.userSegmentationData,
        ...data,
      },
    })),
  setIsSignUpOnboarding: isSignUpOnboarding =>
    set({
      isSignUpOnboarding,
    }),
  setShouldShowReferralModal: shouldShowReferralModal =>
    set({
      shouldShowReferralModal,
    }),
  // We call reset after calling sendMagicLink so we do not upsert data twice.
  // We do not want to clear the isSignUpOnboarding state because we use it to
  // render the onboarding screen. This will be cleared regardless when the user
  // authenticates the magic link email address.
  // We also preserve shouldShowReferralModal because it's set during authentication.
  reset: () =>
    set(state => ({
      ...initialState,
      isSignUpOnboarding: state.isSignUpOnboarding,
      shouldShowReferralModal: state.shouldShowReferralModal,
    })),
}));
